import { RendererManager } from '../engine/RendererManager.js';
import { templateGenerator } from '../engine/TemplateGenerator.js';
import { templateRegistry } from '../data/templateRegistry.js';
import { OBJECT_BY_ID } from '../data/objectCatalog.js';
import { ProjectState } from './ProjectState.js';
import { HistoryManager } from './HistoryManager.js';
import { AutoSaveManager } from './AutoSaveManager.js';
import { SelectionManager } from './SelectionManager.js';
import { PlanEditor } from './PlanEditor.js';
import { validateProject } from './ProjectValidator.js';
import { parseSafeJson, stringifyProject } from '../utils/safeJson.js';
import { createId, slugify } from '../utils/ids.js';
import { downloadFile, escapeHtml } from '../utils/dom.js';
import { showToast } from '../components/Toast.js';
import { t } from '../i18n/index.js';

export class StudioController {
  constructor(root, template, router, { usedFallback = false } = {}) {
    this.root = root;
    this.template = template;
    this.router = router;
    this.usedFallback = usedFallback;
    this.selection = new SelectionManager();
    this.history = new HistoryManager();
    this.autosave = new AutoSaveManager();
    this.renderer = null;
    this.state = null;
    this.planEditor = null;
    this.lastError = null;
    this.cleanup = [];
  }

  async init() {
    try {
      this.#setStatus('Loading template...');
      this.renderer = new RendererManager(this.root.querySelector('[data-renderer-host]'), this.selection);
      this.renderer.onStatus = (message, type) => this.#setStatus(message, type);
      this.renderer.onTransformCommit = (id, before, after) => this.#commitTransform(id, before, after);
      this.renderer.onRecoveryRequested = () => this.state && this.#rebuild();
      this.root.querySelector('[data-quality]').value = this.renderer.quality.level;
      this.root.querySelector('[data-quality-pill]').textContent = this.renderer.quality.settings.label;
      this.root.querySelector('[data-webgpu-label]').textContent = this.renderer.profile.webgpu ? ' · WebGPU available' : '';
      this.#setStatus('Template data found');
      this.#setLoadingDetail('Generating procedural geometry...');
      let projectData;
      const saved = await this.autosave.loadLast();
      if (saved?.templateId === this.template.id && window.confirm(t('recovery.prompt'))) {
        projectData = validateProject(saved);
      } else {
        projectData = templateGenerator.createProjectData(this.template);
      }
      this.state = new ProjectState(projectData);
      this.#bindState();
      this.#setLoadingDetail('Adding meshes to scene...');
      const generated = templateGenerator.groupFromProject(projectData, this.renderer.objectFactory);
      const stats = this.renderer.loadGenerated({ projectData, ...generated });
      this.#bindUi();
      this.#renderFloors();
      this.planEditor = new PlanEditor({
        root: this.root,
        renderer: this.renderer,
        state: this.state,
        history: this.history,
        selection: this.selection,
        onStatus: (message, type) => this.#setStatus(message, type)
      });
      this.planEditor.sync();
      this.#updateHistoryButtons();
      this.#updateDebug();
      this.#setStatus('Template rendered successfully');
      this.root.querySelector('[data-mesh-status]').textContent = `${stats.meshCount} meshes · ${stats.objectCount} objects`;
      this.root.querySelector('[data-canvas-loading]').hidden = true;
      this.autosave.queue(this.state.getSnapshot());
      if (this.usedFallback) showToast(t('studio.fallback'), 'warning', 5200);
    } catch (error) {
      this.#handleError(error);
    }
  }

  dispose() {
    for (const callback of this.cleanup.splice(0)) callback();
    this.planEditor?.dispose();
    this.renderer?.dispose();
  }

  #bindState() {
    const onChange = (event) => {
      const detail = event.detail ?? {};
      try {
        if (detail.type === 'project-settings') {
          this.renderer.requestRender();
        } else if (detail.type === 'entity-updated') {
          const entity = this.state.findEntity(detail.id)?.entity;
          if (!this.renderer.updateEntity(entity)) this.#rebuild();
        } else {
          this.#rebuild();
        }
        this.autosave.queue(this.state.getSnapshot());
        this.#renderFloors();
        this.#renderInspector();
        this.#updateDebug();
      } catch (error) {
        this.#handleError(error);
      }
    };
    this.state.addEventListener('change', onChange);
    this.cleanup.push(() => this.state.removeEventListener('change', onChange));
    const onSelection = () => {
      this.#renderInspector();
      this.#updateMaterialSelection();
    };
    this.selection.addEventListener('change', onSelection);
    this.cleanup.push(() => this.selection.removeEventListener('change', onSelection));
    const onHistory = () => this.#updateHistoryButtons();
    this.history.addEventListener('change', onHistory);
    this.cleanup.push(() => this.history.removeEventListener('change', onHistory));
    const onSave = (event) => {
      const labels = { saving: t('save.saving'), saved: t('save.saved'), offline: t('save.offline'), error: t('save.error') };
      this.root.querySelector('[data-save-status]').textContent = labels[event.detail.status] ?? event.detail.status;
    };
    this.autosave.addEventListener('status', onSave);
    this.cleanup.push(() => this.autosave.removeEventListener('status', onSave));
  }

  #bindUi() {
    for (const button of this.root.querySelectorAll('[data-mode]')) {
      button.addEventListener('click', () => {
        this.root.querySelectorAll('[data-mode]').forEach((item) => item.classList.toggle('is-active', item === button));
        this.root.classList.toggle('walk-mode', button.dataset.mode === 'walk');
        this.renderer.setMode(button.dataset.mode);
        this.planEditor?.setActive(button.dataset.mode === 'plan');
        this.#renderInspector();
        this.#setStatus(`${button.textContent.trim()} active`);
        this.#updateDebug();
      });
    }
    this.root.addEventListener('click', (event) => {
      const action = event.target.closest('[data-action]')?.dataset.action;
      if (action) this.#runAction(action);
      const objectId = event.target.closest('[data-add-object]')?.dataset.addObject;
      if (objectId) this.#addObject(objectId);
      const materialId = event.target.closest('[data-material]')?.dataset.material;
      if (materialId) this.#changeMaterial(materialId);
      const nudge = event.target.closest('[data-nudge]')?.dataset.nudge;
      if (nudge) this.#nudge(nudge);
      const transformMode = event.target.closest('[data-transform-mode]')?.dataset.transformMode;
      if (transformMode) this.renderer.setTransformMode(transformMode);
      const panel = event.target.closest('[data-mobile-panel]')?.dataset.mobilePanel;
      if (panel) this.#setMobilePanel(panel);
      const floorDirection = event.target.closest('[data-walk-floor]')?.dataset.walkFloor;
      if (floorDirection) this.renderer.camera.changeWalkFloor(Number(floorDirection));
    });
    this.root.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-wall-editor]');
      if (!form) return;
      event.preventDefault();
      this.#applyWallGeometry(form);
    });
    const search = this.root.querySelector('[data-object-search]');
    search.addEventListener('input', () => {
      const query = search.value.trim().toLowerCase();
      this.root.querySelectorAll('[data-search-value]').forEach((item) => { item.hidden = Boolean(query && !item.dataset.searchValue.includes(query)); });
    });
    this.root.querySelector('[data-quality]').addEventListener('change', (event) => {
      this.renderer.setQuality(event.target.value);
      this.root.querySelector('[data-quality-pill]').textContent = this.renderer.quality.settings.label;
      this.state.patchProject({ quality: event.target.value });
    });
    this.root.querySelector('[data-toggle-roof]').addEventListener('change', (event) => this.renderer.setRoofVisible(event.target.checked));
    this.root.querySelector('[data-toggle-furniture]').addEventListener('change', (event) => this.renderer.setFurnitureVisible(event.target.checked));
    this.root.querySelector('[data-custom-color]').addEventListener('change', (event) => this.#changeCustomColor(event.target.value));
    this.root.querySelector('[data-floor-list]').addEventListener('change', (event) => {
      const id = event.target.dataset.floorVisible;
      if (!id) return;
      const found = this.state.findEntity(id)?.entity;
      if (!found) return;
      const before = found.visible !== false;
      const after = event.target.checked;
      this.history.execute({ description: 'Change floor visibility', do: () => this.state.setFloorVisibility(id, after), undo: () => this.state.setFloorVisibility(id, before) });
    });
    const file = this.root.querySelector('[data-import-file]');
    file.addEventListener('change', () => this.#importProject(file));
    for (const button of this.root.querySelectorAll('[data-move]')) {
      const key = button.dataset.move;
      const start = (event) => { event.preventDefault(); this.renderer.camera.setTouchMovement(key, true); };
      const stop = () => this.renderer.camera.setTouchMovement(key, false);
      button.addEventListener('pointerdown', start);
      button.addEventListener('pointerup', stop);
      button.addEventListener('pointercancel', stop);
      button.addEventListener('pointerleave', stop);
    }
    this.onKeyDown = (event) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName) || event.target.isContentEditable) return;
      if ((event.ctrlKey || event.metaKey) && event.code === 'KeyZ') { event.preventDefault(); event.shiftKey ? this.history.redo() : this.history.undo(); }
      else if ((event.ctrlKey || event.metaKey) && event.code === 'KeyY') { event.preventDefault(); this.history.redo(); }
      else if ((event.ctrlKey || event.metaKey) && event.code === 'KeyD') { event.preventDefault(); this.#duplicateSelected(); }
      else if (event.code === 'Delete') this.#deleteSelected();
      else if (event.code === 'Escape') this.renderer.clearSelection();
    };
    window.addEventListener('keydown', this.onKeyDown);
    this.cleanup.push(() => window.removeEventListener('keydown', this.onKeyDown));
  }

  #runAction(action) {
    const actions = {
      undo: () => this.history.undo(),
      redo: () => this.history.redo(),
      screenshot: () => this.#exportScreenshot(),
      'export-json': () => downloadFile(`${slugify(this.state.data.name)}.kips.json`, stringifyProject(this.state.getSnapshot()), 'application/json'),
      'import-json': () => this.root.querySelector('[data-import-file]').click(),
      duplicate: () => this.#duplicateSelected(),
      delete: () => this.#deleteSelected(),
      'add-floor': () => this.#addFloor(),
      'duplicate-floor': () => this.#duplicateFloor(),
      'delete-floor': () => this.#deleteFloor(),
      'clear-measurements': () => this.#clearMeasurements(),
      retry: () => this.#retry(),
      'safe-default': () => {
        const fallback = templateRegistry.getTemplatesByCategory('house')[0];
        this.router.navigate(`/studio/house/${fallback.id}`, { replace: true });
      }
    };
    actions[action]?.();
  }

  #rebuild() {
    const stats = this.renderer.rebuild(this.state.data);
    this.root.querySelector('[data-mesh-status]').textContent = `${stats.meshCount} meshes · ${stats.objectCount} objects`;
    this.#updateDebug();
  }

  #commitTransform(id, before, after) {
    const found = this.state.findEntity(id)?.entity;
    if (!found) return;
    this.history.execute({
      description: 'Transform object',
      do: () => this.state.patchEntity(id, after),
      undo: () => this.state.patchEntity(id, before)
    });
  }

  #nudge(direction) {
    const id = this.selection.selectedObjectId;
    const found = this.state.findEntity(id)?.entity;
    const meta = this.renderer.objectMap.get(id)?.userData;
    if (!found?.position || meta?.structural) return;
    const before = { position: [...found.position], rotation: [...(found.rotation ?? [0, 0, 0])], scale: [...(found.scale ?? [1, 1, 1])] };
    const after = structuredClone(before);
    if (direction === 'left') after.position[0] -= 0.25;
    if (direction === 'right') after.position[0] += 0.25;
    if (direction === 'forward') after.position[2] -= 0.25;
    if (direction === 'back') after.position[2] += 0.25;
    if (direction === 'rotate') after.rotation[1] += Math.PI / 12;
    this.history.execute({ description: 'Nudge object', do: () => this.state.patchEntity(id, after), undo: () => this.state.patchEntity(id, before) });
  }

  #changeMaterial(materialId, color = null) {
    const id = this.selection.selectedObjectId;
    const found = this.state.findEntity(id)?.entity;
    if (!found) return showToast(t('studio.materialPrompt'), 'warning');
    const key = found.floorMaterial !== undefined ? 'floorMaterial' : 'material';
    const before = { [key]: found[key], materialColor: found.materialColor };
    const after = { [key]: materialId, materialColor: color };
    this.history.execute({ description: 'Change material', do: () => this.state.patchEntity(id, after), undo: () => this.state.patchEntity(id, before) });
    this.#updateMaterialSelection();
  }

  #changeCustomColor(color) {
    const id = this.selection.selectedObjectId;
    const found = this.state.findEntity(id)?.entity;
    if (!found) return showToast(t('studio.materialPrompt'), 'warning');
    this.#changeMaterial(found.material ?? found.floorMaterial ?? 'wall-warm', color);
  }

  #addObject(catalogId) {
    const catalog = OBJECT_BY_ID.get(catalogId);
    const floorId = this.root.querySelector('[data-active-floor]').value || this.state.data.floors[0].id;
    const floor = this.state.data.floors.find((item) => item.id === floorId) ?? this.state.data.floors[0];
    const object = {
      id: createId(catalog.id), catalogId: catalog.id, name: catalog.name, type: 'furniture', category: catalog.category,
      subcategory: catalog.generatorType, generatorType: catalog.generatorType,
      position: [0, (floor.baseY ?? floor.level * floor.height) + 0.03, 0], rotation: [0, 0, 0], scale: [1, 1, 1], dimensions: [...catalog.dimensions],
      material: catalog.defaultMaterial, materialOverrides: {}, floorId: floor.id, roomId: floor.rooms[0]?.id ?? null,
      locked: false, visible: true, placementRules: { snapToFloor: true }
    };
    this.history.execute({ description: `Add ${catalog.name}`, do: () => this.state.addObject(object, floor.id), undo: () => this.state.removeEntity(object.id) });
    this.renderer.selectById(object.id);
    showToast(`${catalog.name} added`, 'success');
  }

  #duplicateSelected() {
    const id = this.selection.selectedObjectId;
    const found = this.state.findEntity(id);
    const meta = this.renderer.objectMap.get(id)?.userData;
    if (!found || meta?.structural || found.entity.locked) return showToast('Select an editable object first.', 'warning');
    const clone = structuredClone(found.entity);
    clone.id = createId(clone.catalogId ?? 'object');
    if (clone.position) { clone.position[0] += 0.45; clone.position[2] += 0.45; }
    const floorId = found.floor?.id ?? null;
    this.history.execute({ description: 'Duplicate object', do: () => this.state.addObject(clone, floorId), undo: () => this.state.removeEntity(clone.id) });
    this.renderer.selectById(clone.id);
  }

  #deleteSelected() {
    const id = this.selection.selectedObjectId;
    const found = this.state.findEntity(id);
    const meta = this.renderer.objectMap.get(id)?.userData;
    const editablePlanStructure = this.renderer.camera.mode === 'plan'
      && ['wall', 'door', 'window'].includes(meta?.type)
      && !found?.entity.locked;
    if (!found || found.entity.locked || (meta?.structural && !editablePlanStructure)) {
      return showToast('This structural or locked object cannot be deleted in the current mode.', 'warning');
    }
    if (meta?.type === 'wall' && (found.floor?.walls?.length ?? 0) <= 4) {
      return showToast('Keep at least four walls on each floor.', 'warning');
    }
    const entity = structuredClone(found.entity);
    const floorId = found.floor?.id ?? null;
    const key = found.key;
    const index = found.index;
    const dependents = meta?.type === 'wall' ? ['doors', 'windows'].flatMap((collectionKey) =>
      (found.floor?.[collectionKey] ?? [])
        .map((item, itemIndex) => ({ item, itemIndex }))
        .filter(({ item }) => item.wallId === id)
        .map(({ item, itemIndex }) => ({ entity: structuredClone(item), key: collectionKey, index: itemIndex }))
    ) : [];
    this.history.execute({
      description: `Delete ${meta?.type ?? 'object'}`,
      do: () => {
        this.renderer.clearSelection();
        for (const dependent of dependents) this.state.removeEntity(dependent.entity.id);
        this.state.removeEntity(id);
      },
      undo: () => {
        this.state.insertEntity(floorId, key, entity, index);
        for (const dependent of dependents) this.state.insertEntity(floorId, dependent.key, dependent.entity, dependent.index);
      }
    });
  }

  #applyWallGeometry(form) {
    const id = this.selection.selectedObjectId;
    const found = this.state.findEntity(id);
    if (!found?.floor || found.entity.type !== 'wall') return showToast('Select a wall in 2D Plan mode first.', 'warning');
    const value = (name) => Number(form.elements.namedItem(name)?.value);
    const numbers = ['startX', 'startZ', 'endX', 'endZ', 'height', 'thickness'].map(value);
    if (numbers.some((item) => !Number.isFinite(item))) return showToast('Wall values must be valid numbers.', 'warning');
    let start = [numbers[0], numbers[1]];
    let end = [numbers[2], numbers[3]];
    if (this.planEditor) {
      start = this.planEditor.snapCoordinates(start);
      end = this.planEditor.snapCoordinates(end, start);
    }
    const validation = this.planEditor?.snap.validateWall(start, end) ?? { valid: true };
    if (!validation.valid) return showToast(validation.reason, 'warning');
    const before = {
      start: [...found.entity.start],
      end: [...found.entity.end],
      height: found.entity.height,
      thickness: found.entity.thickness
    };
    const after = {
      start,
      end,
      height: Math.max(1, Math.min(numbers[4], Math.max(1, found.floor.height - 0.05))),
      thickness: Math.max(0.05, Math.min(numbers[5], 0.6))
    };
    this.history.execute({
      description: 'Edit wall geometry',
      do: () => this.state.patchEntity(id, after, 'wall-updated'),
      undo: () => this.state.patchEntity(id, before, 'wall-updated')
    });
    showToast('Wall geometry updated.', 'success');
  }

  #renderInspector() {
    const panel = this.root.querySelector('[data-inspector]');
    const id = this.selection.selectedObjectId;
    const found = this.state?.findEntity(id);
    if (!found) {
      panel.innerHTML = `<p class="panel-placeholder">${t('studio.selectPrompt')}</p>`;
      return;
    }
    const entity = found.entity;
    const meta = this.renderer.objectMap.get(id)?.userData ?? {};
    const position = entity.position?.map((value) => Number(value).toFixed(2)).join(', ') ?? 'Structural segment';
    const wallEditor = meta.type === 'wall' && this.renderer.camera.mode === 'plan' ? `<form class="wall-editor" data-wall-editor><strong>Edit wall endpoints</strong><div class="wall-coordinate-grid"><label>Start X<input name="startX" type="number" step="0.25" value="${Number(entity.start[0]).toFixed(2)}" /></label><label>Start Z<input name="startZ" type="number" step="0.25" value="${Number(entity.start[1]).toFixed(2)}" /></label><label>End X<input name="endX" type="number" step="0.25" value="${Number(entity.end[0]).toFixed(2)}" /></label><label>End Z<input name="endZ" type="number" step="0.25" value="${Number(entity.end[1]).toFixed(2)}" /></label><label>Height<input name="height" type="number" min="1" max="12" step="0.05" value="${Number(entity.height).toFixed(2)}" /></label><label>Thickness<input name="thickness" type="number" min="0.05" max="0.6" step="0.01" value="${Number(entity.thickness).toFixed(2)}" /></label></div><div class="wall-editor-actions"><button type="submit">Apply geometry</button><button class="danger" type="button" data-action="delete">${t('studio.delete')}</button></div></form>` : '';
    panel.innerHTML = `<div class="selected-heading"><span class="selected-type">${escapeHtml(meta.type ?? entity.type ?? 'object')}</span><h3>${escapeHtml(entity.name ?? entity.id)}</h3></div><dl class="inspector-data"><div><dt>ID</dt><dd title="${escapeHtml(entity.id)}">${escapeHtml(entity.id.slice(-24))}</dd></div><div><dt>Position</dt><dd>${position}</dd></div><div><dt>Material</dt><dd>${escapeHtml(entity.material ?? entity.floorMaterial ?? '—')}</dd></div><div><dt>Floor</dt><dd>${escapeHtml(found.floor?.name ?? 'Site')}</dd></div></dl>${!meta.structural ? `<div class="transform-tools"><div><button type="button" data-transform-mode="translate">${t('studio.translate')}</button><button type="button" data-transform-mode="rotate">${t('studio.rotate')}</button><button type="button" data-transform-mode="scale">${t('studio.scale')}</button></div><div class="nudge-grid"><button type="button" data-nudge="forward">▲</button><button type="button" data-nudge="left">◀</button><button type="button" data-nudge="rotate">↻</button><button type="button" data-nudge="right">▶</button><button type="button" data-nudge="back">▼</button></div><div class="inspector-actions"><button type="button" data-action="duplicate">${t('studio.duplicate')}</button><button class="danger" type="button" data-action="delete">${t('studio.delete')}</button></div></div>` : wallEditor || '<p class="structural-note">Structural geometry can be selected and re-materialed; transforms are protected.</p>'}`;
  }

  #updateMaterialSelection() {
    const id = this.selection.selectedObjectId;
    const entity = this.state?.findEntity(id)?.entity;
    const materialId = entity?.material ?? entity?.floorMaterial;
    this.root.querySelector('[data-material-message]').hidden = Boolean(entity);
    this.root.querySelectorAll('[data-material]').forEach((button) => button.classList.toggle('is-active', button.dataset.material === materialId));
    if (entity?.materialColor) this.root.querySelector('[data-custom-color]').value = entity.materialColor;
  }

  #renderFloors() {
    if (!this.state) return;
    const select = this.root.querySelector('[data-active-floor]');
    const previous = select.value;
    select.innerHTML = this.state.data.floors.map((floor) => `<option value="${floor.id}">${escapeHtml(floor.name)}</option>`).join('');
    if (this.state.data.floors.some((floor) => floor.id === previous)) select.value = previous;
    const list = this.root.querySelector('[data-floor-list]');
    list.innerHTML = this.state.data.floors.map((floor) => `<label><span>${escapeHtml(floor.name)} <small>Level ${floor.level}</small></span><input type="checkbox" data-floor-visible="${floor.id}" ${floor.visible !== false ? 'checked' : ''} /></label>`).join('');
  }

  #addFloor() {
    let created = null;
    this.history.execute({ description: 'Add floor', do: () => { created = created ? this.state.insertFloor(created) ?? created : this.state.addFloor(); }, undo: () => created && this.state.deleteFloor(created.id) });
  }

  #duplicateFloor() {
    const sourceId = this.root.querySelector('[data-active-floor]').value;
    let created = null;
    this.history.execute({ description: 'Duplicate floor', do: () => { created = created ? this.state.insertFloor(created) ?? created : this.state.duplicateFloor(sourceId); }, undo: () => created && this.state.deleteFloor(created.id) });
  }

  #deleteFloor() {
    const id = this.root.querySelector('[data-active-floor]').value;
    if (this.state.data.floors.length <= 1) return showToast('A project must keep at least one floor.', 'warning');
    let removed = null;
    this.history.execute({ description: 'Delete floor', do: () => { removed = this.state.deleteFloor(id) ?? removed; }, undo: () => removed && this.state.insertFloor(removed) });
  }

  #clearMeasurements() {
    const floor = this.planEditor?.getActiveFloor();
    if (!floor) return;
    const before = structuredClone(floor.measurements ?? []);
    if (!before.length) return showToast('No saved measurements on this floor.', 'warning');
    this.history.execute({
      description: 'Clear floor measurements',
      do: () => this.state.replaceFloorCollection(floor.id, 'measurements', [], 'measurements-cleared'),
      undo: () => this.state.replaceFloorCollection(floor.id, 'measurements', before, 'measurements-restored')
    });
    showToast('Floor measurements cleared.', 'success');
  }

  async #exportScreenshot() {
    try {
      const blob = await this.renderer.exportPng();
      downloadFile(`${slugify(this.state.data.name)}.png`, blob, 'image/png');
      showToast('PNG exported', 'success');
    } catch (error) { this.#handleError(error); }
  }

  async #importProject(input) {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const imported = validateProject(parseSafeJson(await file.text()));
      const before = this.state.getSnapshot();
      this.history.execute({ description: 'Import project', do: () => { this.renderer.clearSelection(); this.state.replaceProject(imported, 'project-imported'); }, undo: () => this.state.replaceProject(before, 'project-import-undone') });
      showToast('Project imported', 'success');
    } catch (error) {
      showToast(`${t('error.invalidProject')} ${error.message}`, 'error', 6000);
    } finally { input.value = ''; }
  }

  #retry() {
    try {
      this.root.querySelector('[data-render-error]').hidden = true;
      this.#rebuild();
      this.#setStatus('Template rendered successfully');
    } catch (error) { this.#handleError(error); }
  }

  #setMobilePanel(panel) {
    this.root.dataset.mobilePanelActive = panel;
    this.root.querySelectorAll('[data-mobile-panel]').forEach((button) => button.classList.toggle('is-active', button.dataset.mobilePanel === panel));
  }

  #updateHistoryButtons() {
    this.root.querySelector('[data-action="undo"]').disabled = !this.history.canUndo;
    this.root.querySelector('[data-action="redo"]').disabled = !this.history.canRedo;
  }

  #setLoadingDetail(message) {
    const target = this.root.querySelector('[data-loading-status]');
    if (target) target.textContent = message;
  }

  #setStatus(message, type = 'info') {
    const target = this.root.querySelector('[data-render-status]');
    if (target) { target.textContent = message; target.dataset.status = type; }
    this.#setLoadingDetail(message);
    this.#updateDebug();
  }

  #updateDebug() {
    if (!this.renderer) return;
    const debug = this.renderer.getDebug();
    const values = {
      'Active category': this.state?.data.categoryId ?? this.template.categoryId,
      'Active template': this.state?.data.templateId ?? this.template.id,
      'Template name': this.state?.data.name ?? this.template.name,
      'Generator': this.state?.data.metadata?.generatorType ?? this.template.generatorType,
      'Render status': debug.status ?? (this.lastError ? 'failed' : 'loading'),
      'Objects': debug.objectCount ?? 0,
      'Meshes': debug.meshCount ?? 0,
      'Visible meshes': debug.visibleMeshCount ?? 0,
      'Materials': debug.materialCount ?? 0,
      'Floors': debug.floorCount ?? 0,
      'Walls': debug.wallCount ?? 0,
      'Furniture': debug.furnitureCount ?? 0,
      'Scene children': debug.sceneChildren ?? 0,
      'Bounding size': debug.boundingBox?.size?.map((value) => Number(value).toFixed(2)).join(' × ') ?? '—',
      'Camera': debug.camera?.position?.join(', ') ?? '—',
      'Orbit target': debug.camera?.target?.join(', ') ?? '—',
      'Memory budget': `${debug.memory?.status ?? '—'} (${debug.meshCount ?? 0}/${debug.memory?.budget ?? '—'})`,
      'Last error': this.lastError?.message ?? 'None'
    };
    const list = this.root.querySelector('[data-debug-list]');
    if (list) list.innerHTML = Object.entries(values).map(([key, value]) => `<div><dt>${escapeHtml(key)}</dt><dd>${escapeHtml(String(value))}</dd></div>`).join('');
  }

  #handleError(error) {
    this.lastError = error instanceof Error ? error : new Error(String(error));
    const overlay = this.root.querySelector('[data-render-error]');
    overlay.hidden = false;
    overlay.querySelector('[data-render-error-message]').textContent = this.lastError.message;
    this.root.querySelector('[data-canvas-loading]').hidden = true;
    this.#setStatus('Template render failed', 'error');
    this.#updateDebug();
    console.error('[KIPS DESIGN]', this.lastError);
  }
}
